import React from 'react';
import { GoBackButtons } from '../views/go-back-buttons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import '../styles/result.scss';
import { formatFloatingNumber, getQuery } from '../../shared/util/functions';
import { ResultEyeTest } from '../views/result-eye-test';
import { ResultContrastTest } from '../views/result-contrast-test';
import { ResultMichelsonTest } from '../views/result-michelson-test';
import { ResultSchrift } from '../views/result-schrift';
import { postTelemetry } from '../util/axios';
import { StringMap, VidebisTest } from '../../shared/util/types';
import { TestLogicFactory } from '../../eye-test/logic/test-logic-factory';
import { GlobalLogic } from '../../shared/logic/global-logic';
import { DebugBox } from '../../shared/components/debug-box/debug-box';
import { AllTestsResult } from '../views/all-tests-result';
import {
	getContrastResultTelemetryData,
	getFinalTelemetryData,
	getMichelsonResultTelemetryData,
	getVergroesserungsbedarfTelemetryData,
	getVisusResultTelemetryData
} from '../util/telemetry';
import { IFinalTelemetryContainer, ITelemetryData } from '../util/typings';
import { RootController } from '../../eye-test/controller/root.controller';
import axios from 'axios';

library.add(faCheck);

interface Props {
	history: any;
	location: any;
}

export class ResultController extends React.PureComponent<Props> {
	private _eyeTestLogic = TestLogicFactory.getInstanceOf('eye-test-logic');
	private _contrastTestLogic = TestLogicFactory.getInstanceOf('contrast-test-logic');
	private _globalLogic = GlobalLogic.getInstance();

	getDebugContent = (): StringMap<string | string[]> => {
		const visus = formatFloatingNumber(
			this._eyeTestLogic.smallestCorrectLandolt.visusOneMeter * this._globalLogic.distance / 100
		);
		const lastCorrectEyeTest: string =
			'Id: ' + this._eyeTestLogic.smallestCorrectLandolt.id +
			', size: ' + this._eyeTestLogic.smallestCorrectLandolt.baseSize +
			', visus on 1m: ' + this._eyeTestLogic.smallestCorrectLandolt.visusOneMeter;

		const lastCorrectContrastTest: string =
			'Font color: ' + this._contrastTestLogic.smallestCorrectLandolt.color;
		return ({
			'Arm length': this._globalLogic.distance.toFixed(3).toString(),
			'Visus': visus,
			'Last correct eye-test': lastCorrectEyeTest,
			'Last correct contrast-test': lastCorrectContrastTest
		});
	};

	componentDidMount(): void {
		postTelemetry('result', this.getTelemetryData());

		if (this.getFromTest() === 'all-tests') {
			const postFinalResultMessage = () => {
				const body = { "end": "done" };
				axios.post(`https://www.videbis.at/api/v1/testdata/st/${RootController.globalUserID}`,
					body)
					.then(res => {
						console.log('Server replied.');
						console.log(res);
					})
					.catch(error => {
						console.log(error.response);
					});
			};

			if (!RootController.globalUserID) {
				const body = {
					idRequest: true,
					userAgent: navigator.userAgent,
					height: window.screen.height,
					width: window.screen.width,
					url: window.location.href
				};

				axios.post('https://www.videbis.at/api/v1/testdata/st', body)
					.then(response => {
						console.log('Server replied on ID request!', response.data.id);
						RootController.globalUserID = response.data.id;
						postFinalResultMessage();
					})
					.catch(err => {
						console.log(err.response);
					});
				return;
			} else {
				postFinalResultMessage();
			}
		}
	}

	getTelemetryData = (): ITelemetryData | IFinalTelemetryContainer => {
		const fromTest = this.getFromTest();
		switch (fromTest) {
			case 'eye-test':
				return getVisusResultTelemetryData();
			case 'contrast-test':
				return getContrastResultTelemetryData();
			case 'michelson-test':
				return getMichelsonResultTelemetryData();
			case 'text-test':
				return getVergroesserungsbedarfTelemetryData();
			case 'all-tests':
			default:
				return getFinalTelemetryData();
		}
	};

	getResult = (): JSX.Element | null => {
		const fromTest = this.getFromTest();

		if (!fromTest) {
			return null;
		}

		switch (fromTest) {
			case 'eye-test':
				return <ResultEyeTest />;
			case 'contrast-test':
				return <ResultContrastTest />;
			case 'michelson-test':
				return <ResultMichelsonTest />;
			case 'text-test':
				return <ResultSchrift />;
			case 'all-tests':
				return (
					<div className="result-all">
						<AllTestsResult />
					</div>
				);
			default:
				return null;
		}
	};

	getFromTest = (): (VidebisTest | 'all-tests') | null => {
		const { from } = getQuery(this.props.location.search) ?? null;
		return from;
	};

	render(): any {
		const result = this.getResult();
		return (
			<main>
				<h1>Fertig!</h1>
				<div className={'result-description'}>
					{result}
				</div>

				<div className={'result-description__icon'}>
					<FontAwesomeIcon icon={'check'} />
				</div>

				<p className={'result-description disclaimer standard-text'}>
					Bitte beachten Sie, dass das Ergebnis nur einen Richtwert darstellt. Der Online-Sehtest ersetzt
					keinen professionellen Sehtest. Vereinbaren Sie jetzt einen Termin für
					unseren <strong>kostenfreien Profi-Sehtest!</strong>
				</p>
				<p className={'disclaimer standard-text'}>
					<i>VIDEBIS GmbH, Schlosshofer Straße 6, 1210 Wien <br /> Tel.: +43 1 2788333</i>
				</p>

				<GoBackButtons history={this.props.history} bgColor={'#ffffff'} txtColor={'#0c23fb'} />
				<DebugBox content={this.getDebugContent()} />
			</main>
		);
	}
}
